import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/qhearts/Navbar";
import HeroArt from "../components/qhearts/HeroArt";
import QHeartProduct from "../components/qhearts/QHeartProduct";
import WaitlistForm from "../components/qhearts/WaitlistForm";
import ComingSoonDialog from "../components/qhearts/ComingSoonDialog";
import BrandHeart from "../components/qhearts/BrandHeart";
import EventVideo from "../components/qhearts/EventVideo";
import { useCookieConsent } from "../context/CookieContext";
import { motion } from "framer-motion";
import {
  Heart,
  Sparkles,
  Repeat,
  ShieldCheck,
  ScanFace,
  Eye,
  HandHeart,
  Check,
  X,
  Apple,
  Smartphone,
  Quote,
  Star,
  ArrowRight,
} from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { Button } from "../components/ui/button";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const Section = ({ id, children, className = "" }) => (
  <section
    id={id}
    className={`py-24 sm:py-32 px-6 sm:px-8 lg:px-12 ${className}`}
  >
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Overline = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.22em] font-semibold text-qh-rose">
    {children}
  </span>
);

export default function Landing() {
  const [showComingSoon, setShowComingSoon] = useState(false);
  const { openSettings } = useCookieConsent();

  // noindex meta — keep preview out of search engines
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  const openComingSoon = () => setShowComingSoon(true);

  return (
    <div className="bg-white text-qh-ink min-h-screen font-body" data-testid="landing-page">
      <Toaster richColors position="top-center" />
      <Navbar onCTAClick={openComingSoon} />
      <ComingSoonDialog open={showComingSoon} onOpenChange={setShowComingSoon} />

      {/* HERO */}
      <section
        className="relative pt-40 sm:pt-48 pb-20 sm:pb-24 px-6 sm:px-8 lg:px-12 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Soft background wash */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#FFFBFB] via-white to-white" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <Overline>Kein Swipe, ein echter Moment.</Overline>
            <h1
              className="mt-5 font-display font-medium text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-qh-ink"
              data-testid="hero-headline"
            >
              Finde, was dein{" "}
              <span className="italic text-qh-rose">Herz</span>
              <br className="hidden sm:block" /> wirklich sucht.
            </h1>
            <p className="mt-6 text-lg text-qh-slate leading-relaxed max-w-xl">
              QHearts ist die Dating-Plattform für Menschen, die wieder richtig
              ankommen wollen. Verifiziert. Bewusst. Inklusiv. Keine Endlos-Feeds,
              keine Bots — nur Herzen, die etwas bedeuten.
            </p>

            <div className="mt-8">
              <WaitlistForm />
              <p className="mt-3 text-xs text-qh-slate" data-testid="hero-privacy-note">
                Kostenloser Early Access. Niemals Spam. Jederzeit abmeldbar.
              </p>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-qh-slate" data-testid="hero-social-proof">
              <div className="flex -space-x-2">
                {["#FFE4E6", "#E11D48", "#0A0A0A", "#52525B"].map((c) => (
                  <span
                    key={c}
                    className="inline-flex w-8 h-8 rounded-full border-2 border-white"
                    style={{ background: c }}
                  />
                ))}
              </div>
              <div>
                <span className="font-semibold text-qh-ink">12.400+</span>{" "}
                Menschen warten schon auf ihre Einladung.
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
            className="relative"
          >
            <HeroArt />
          </motion.div>
        </div>
      </section>

      {/* DAS QHEART — physisches Produkt */}
      <QHeartProduct />

      {/* QHEARTS SYSTEM */}
      <Section id="how" className="bg-[#FFFBFB]">
        {/* Intro */}
        <motion.div {...fadeUp} className="max-w-3xl">
          <Overline>Das QHearts-System</Overline>
          <h2 className="mt-5 font-display font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight text-qh-ink leading-[1.05]">
            Echte Menschen.<br />
            Echte <span className="italic text-qh-rose">Emotionen</span>.<br />
            Echte Begegnungen.
          </h2>
          <div className="mt-8 space-y-5 text-lg text-qh-slate leading-relaxed">
            <p>
              Bei QHearts geht es nicht ums Swipen. Es geht darum, jemandem bewusst
              zu zeigen:
            </p>
            <p className="text-qh-ink font-medium italic font-display text-2xl sm:text-3xl text-qh-rose">
              Du bist mir aufgefallen.
            </p>
            <p>
              Mit einem QHeart entscheidest du selbst, wem du begegnen möchtest —
              ohne Algorithmus, ohne Oberflächlichkeit, ohne Zeitdruck.
            </p>
          </div>
        </motion.div>

        {/* Journey */}
        <div className="mt-20 sm:mt-28" data-testid="how-it-works">
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8 max-w-6xl mx-auto">
            {[
              {
                num: "01",
                icon: Eye,
                title: "Der Moment, der alles verändert.",
                body:
                  "Du siehst jemanden. Nicht auf einem Bildschirm — im echten Leben. Ein Blick. Ein Gefühl. Ein Moment, der bleibt.",
                image: "/assets/qh-sight.jpg",
              },
              {
                num: "02",
                icon: BrandHeart,
                title: "Ein Moment mit Bedeutung.",
                body:
                  "Du lässt ihn nicht einfach verstreichen. Du entscheidest dich bewusst — und wählst ein QHeart. Mit deinem Profil und Worten, die wirklich von dir kommen.",
                image: "/assets/qh-customize.jpg",
              },
              {
                num: "03",
                icon: HandHeart,
                title: "Der Moment der Übergabe.",
                body:
                  "Ohne Druck. Ohne Erwartung. Du gibst ein stilles, ehrliches Zeichen: Diese Begegnung bedeutet mir etwas.",
                image: "/assets/qh-uebergabe.jpg",
              },
              {
                num: "04",
                icon: Sparkles,
                title: "Der Moment der Entscheidung.",
                body:
                  "Die Person nimmt sich Zeit. Liest deine Worte. Spürt, wer du bist. Und entscheidet selbst, ob ein Kennenlernen entstehen soll.",
                image: "/assets/qh-scan.jpg",
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: "easeOut" }}
                className="relative rounded-2xl border border-qh-line bg-white p-8 sm:p-10 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(225,29,72,0.18)] transition-all duration-500 flex flex-col"
                data-testid={`how-step-${i + 1}`}
              >
                <div className="flex items-center gap-4">
                  <span className="font-display text-sm text-qh-rose tracking-[0.22em]">
                    {item.num}
                  </span>
                  <span className="h-px flex-1 bg-qh-line" />
                  <span className="h-11 w-11 rounded-full bg-[#FFE4E6] flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-qh-rose" strokeWidth={1.5} />
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl sm:text-3xl font-medium text-qh-ink leading-tight min-h-[2.4em]">
                  {item.title}
                </h3>
                <p className="mt-4 text-qh-slate leading-relaxed flex-1">
                  {item.body}
                </p>
                <div className="mt-8 rounded-2xl overflow-hidden aspect-[2/3] bg-qh-blush">
                  <img
                    src={item.image}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                    draggable="false"
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Pull quote */}
          <motion.blockquote
            {...fadeUp}
            className="mt-16 sm:mt-20 max-w-3xl"
            data-testid="how-pullquote"
          >
            <p className="font-display italic text-3xl sm:text-4xl leading-tight text-qh-ink">
              Kein Swipen. Kein Ghosting.<br />
              Nur echte <span className="text-qh-rose">Entscheidungen</span>{" "}
              zwischen echten Menschen.
            </p>
          </motion.blockquote>
        </div>
      </Section>

      {/* SAFETY */}
      <Section id="safety">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div {...fadeUp}>
            <Overline>Sicherheit & Verifizierung</Overline>
            <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-qh-ink">
              Echte Menschen.<br />{" "}
              <span className="italic text-qh-rose">Keine Fake-Profile.</span>
            </h2>
            <p className="mt-5 text-lg text-qh-slate leading-relaxed max-w-xl">
              Bei QHearts wird jede Person verifiziert. Gesicht und Ausweis müssen
              übereinstimmen.
            </p>
            <p className="mt-3 text-lg text-qh-slate leading-relaxed max-w-xl">
              So entstehen echte Begegnungen — ohne Zweifel, ohne Unsicherheit.
            </p>

            <ul className="mt-10 space-y-5" data-testid="safety-list">
              {[
                {
                  icon: Eye,
                  title: "Selfie-Verifizierung in Echtzeit",
                  body: "Dein Verifizierungs-Herz bekommst du erst, wenn dein biometrischer Scan wirklich mit deinen Profilfotos übereinstimmt.",
                },
                {
                  icon: ShieldCheck,
                  title: "Null Toleranz. Menschliche Moderation.",
                  body: "Belästigung, Catfishing und toxisches Verhalten verschwinden in Stunden — nicht in Tagen.",
                },
                {
                  icon: BrandHeart,
                  title: "Keine Vergabe von QHearts ohne Verifizierung.",
                  body: "Nur verifizierte User können QHearts vergeben. So stellen wir sicher, dass hinter jedem Herz ein echter Mensch steht.",
                },
              ].map((f) => (
                <li key={f.title} className="flex gap-4">
                  <span className="shrink-0 h-11 w-11 rounded-full border border-qh-line flex items-center justify-center bg-white">
                    <f.icon className="w-5 h-5 text-qh-rose" strokeWidth={1.5} />
                  </span>
                  <div>
                    <h4 className="font-display text-xl font-medium text-qh-ink">
                      {f.title}
                    </h4>
                    <p className="mt-1 text-qh-slate leading-relaxed">{f.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Visual — real verified profiles, stacked */}
          <motion.div
            {...fadeUp}
            className="relative"
            data-testid="safety-visual"
          >
            <div
              className="relative mx-auto max-w-md aspect-[5/6] group/swap"
              data-testid="safety-cards"
            >
              {/* Back card — Mats (moves forward on group hover) */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: 8 }}
                whileInView={{ opacity: 1, y: 0, rotate: 6 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
                className="absolute top-[6%] right-0 w-[72%] rounded-3xl border border-qh-line bg-white p-4 shadow-[0_30px_60px_-30px_rgba(10,10,10,0.25)] transition-all duration-700 ease-out z-10 group-hover/swap:z-30 group-hover/swap:top-0 group-hover/swap:right-auto group-hover/swap:left-0 group-hover/swap:w-[78%] group-hover/swap:rotate-[-4deg] group-hover/swap:shadow-[0_40px_80px_-30px_rgba(225,29,72,0.25)]"
                data-testid="safety-card-back"
              >
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-[#FFFBFB]">
                  <img
                    src="/assets/profile-mats.jpg"
                    alt="Mats — verifiziertes Profil"
                    draggable="false"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-3 right-3 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 border border-qh-line">
                    <BrandHeart className="w-3.5 h-3.5" alt="QHearts" />
                    <span className="text-[10px] font-semibold tracking-wide text-qh-ink">
                      VERIFIZIERT
                    </span>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between px-1">
                  <div>
                    <p className="font-display text-sm font-medium text-qh-ink">
                      Mats, 35
                    </p>
                    <p className="text-[11px] text-qh-slate">Mannheim</p>
                  </div>
                  <BrandHeart className="w-4 h-4" alt="QHearts" />
                </div>
              </motion.div>

              {/* Front card — Yvonne (slides behind on group hover) */}
              <motion.div
                initial={{ opacity: 0, y: 30, rotate: -8 }}
                whileInView={{ opacity: 1, y: 0, rotate: -4 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="absolute top-0 left-0 w-[78%] rounded-3xl border border-qh-line bg-white p-5 shadow-[0_40px_80px_-30px_rgba(225,29,72,0.25)] transition-all duration-700 ease-out z-20 group-hover/swap:z-10 group-hover/swap:top-[6%] group-hover/swap:left-auto group-hover/swap:right-0 group-hover/swap:w-[72%] group-hover/swap:rotate-[6deg] group-hover/swap:shadow-[0_30px_60px_-30px_rgba(10,10,10,0.25)] group-hover/swap:p-4"
                data-testid="safety-card-front"
              >
                <div className="relative rounded-2xl overflow-hidden aspect-[4/5] bg-[#FFFBFB]">
                  <img
                    src="/assets/profile-1.jpeg"
                    alt="Yvonne — verifiziertes Profil"
                    draggable="false"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute top-4 right-4 flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 border border-qh-line shadow-sm">
                    <BrandHeart className="w-4 h-4" alt="QHearts" />
                    <span className="text-[11px] font-semibold tracking-wide text-qh-ink">
                      VERIFIZIERT
                    </span>
                  </div>
                  {/* Overlay — verification info */}
                  <div className="absolute inset-x-3 bottom-3 rounded-2xl bg-white/90 backdrop-blur-md px-3 py-2.5 border border-qh-line">
                    <p className="text-[11px] font-semibold text-qh-ink leading-tight">
                      Diese Person wurde erfolgreich verifiziert.
                    </p>
                    <p className="text-[10px] text-qh-slate mt-0.5 leading-tight">
                      Biometrischer Scan & Profilfotos stimmen überein.
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between px-1">
                  <div>
                    <p className="font-display text-base font-medium text-qh-ink">
                      Yvonne, 32
                    </p>
                    <p className="text-xs text-qh-slate">Dillingen/Saar</p>
                  </div>
                  <BrandHeart className="w-5 h-5" alt="QHearts" />
                </div>
              </motion.div>

              {/* Floating chip — Selfie-Check */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 }}
                className="absolute -left-3 sm:-left-6 top-[42%] rounded-full border border-qh-line bg-white pl-3 pr-4 py-2 shadow-[0_20px_40px_-20px_rgba(10,10,10,0.15)]"
              >
                <div className="flex items-center gap-2">
                  <span className="h-5 w-5 rounded-full bg-[#FFE4E6] flex items-center justify-center">
                    <ScanFace
                      className="w-3 h-3 text-qh-rose"
                      strokeWidth={2.25}
                    />
                  </span>
                  <span className="text-xs font-medium text-qh-ink">
                    Selfie-Check
                  </span>
                  <Check className="w-3.5 h-3.5 text-qh-rose" strokeWidth={3} />
                </div>
              </motion.div>

            </div>
          </motion.div>
        </div>
      </Section>

      {/* BENEFITS */}
      <Section id="benefits" className="bg-[#FFFBFB]">
        <motion.div {...fadeUp} className="max-w-2xl">
          <Overline>Warum QHearts</Overline>
          <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-qh-ink">
            Dating-Apps haben uns erschöpft.<br />
            <span className="italic text-qh-rose">Wir geben der Liebe ihre Ruhe zurück.</span>
          </h2>
        </motion.div>

        {/* Bento */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-6 gap-5" data-testid="benefits-bento">
          {/* Hero Statement */}
          <motion.div
            {...fadeUp}
            className="md:col-span-6 rounded-3xl border border-qh-line bg-white p-10 sm:p-14 lg:p-16 hover:-translate-y-1 transition-all duration-500 text-center"
            data-testid="benefit-hero"
          >
            <img
              src="/assets/qhearts-animation.svg"
              alt=""
              aria-hidden="true"
              className="h-24 sm:h-28 w-auto mx-auto select-none"
              draggable="false"
            />
            <h3 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-qh-ink leading-[1.05]">
              Qualität statt Quantität.
            </h3>
            <p className="mt-5 font-display italic text-xl sm:text-2xl text-qh-rose leading-snug">
              Kein Swipen. Keine Matches sammeln. Keine Spielchen.
            </p>
          </motion.div>

          {/* Journey cards 01–06 */}
          {[
            {
              num: "01",
              title: "Kein künstlicher Druck.",
              body:
                "Keine Algorithmen, die dich steuern. Keine Limits, die dich einschränken. Du bewegst dich frei.",
              icon: "/assets/icons/01-no-pressure.png",
            },
            {
              num: "02",
              title: "Komplett kostenlos.",
              body:
                "Du kannst dich frei bewegen, entdecken und entscheiden — ohne Paywall, ohne Werbung.",
              icon: "/assets/icons/02-free.png",
            },
            {
              num: "03",
              title: "Ein bewusster Schritt.",
              body:
                "Wenn du einen echten Schritt machen willst, nutzt du ein QHeart. Klar. Ehrlich. Bewusst.",
              icon: "/assets/icons/03-step.png",
            },
            {
              num: "04",
              title: "Dein Icebreaker.",
              body:
                "Ein QHeart ist mehr als eine Nachricht — es ist ein bewusster Moment. Eine echte Entscheidung.",
              icon: "/assets/icons/04-icebreaker.png",
            },
            {
              num: "05",
              title: "Du entscheidest.",
              body:
                "Du bestimmst selbst, wem du ein QHeart gibst — und damit, welche Begegnungen in dein Leben kommen.",
              icon: "/assets/icons/05-decide.png",
            },
            {
              num: "06",
              title: "Echte Impulse.",
              body:
                "Keine Likes. Kein Dislike. Kein endloses Scrollen. Sondern echte Impulse, die du selbst setzt.",
              icon: "/assets/icons/06-impulses.png",
            },
          ].map((c, i) => (
            <motion.div
              key={c.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: "easeOut" }}
              className="md:col-span-2 rounded-3xl border border-qh-line bg-white p-8 sm:p-10 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(225,29,72,0.18)] transition-all duration-500 flex flex-col"
              data-testid={`benefit-step-${c.num}`}
            >
              <div className="flex items-center gap-4">
                <span className="font-display text-sm text-qh-rose tracking-[0.22em]">
                  {c.num}
                </span>
                <span className="h-px flex-1 bg-qh-line" />
              </div>
              <div className="mt-6 h-16 flex items-center justify-center">
                <img
                  src={c.icon}
                  alt=""
                  aria-hidden="true"
                  className="max-h-16 w-auto object-contain select-none"
                  draggable="false"
                />
              </div>
              <h3 className="mt-5 font-display text-2xl sm:text-3xl font-medium text-qh-ink leading-tight min-h-[2.4em] text-center">
                {c.title}
              </h3>
              <p className="mt-4 text-qh-slate leading-relaxed flex-1 text-center">
                {c.body}
              </p>
            </motion.div>
          ))}

          {/* Conclusion card 07 */}
          <motion.div
            {...fadeUp}
            className="md:col-span-6 rounded-3xl bg-qh-ink text-white p-10 sm:p-14 lg:p-16 hover:-translate-y-1 transition-all duration-500"
            data-testid="benefit-step-07"
          >
            <div className="flex items-center gap-4">
              <span className="font-display text-sm text-qh-rose tracking-[0.22em]">
                07
              </span>
              <span className="h-px flex-1 bg-white/15" />
              <img
                src="/assets/icons/07-match.png"
                alt=""
                aria-hidden="true"
                className="h-14 w-auto object-contain select-none shrink-0"
                draggable="false"
              />
            </div>
            <h3 className="mt-6 font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-[1.15] md:whitespace-nowrap">
              Deine Matches entstehen nicht durch Zufall —
            </h3>
            <p className="mt-3 font-display italic text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-qh-rose tracking-tight leading-[1.15] md:whitespace-nowrap">
              sondern durch deine Entscheidung.
            </p>
            <p className="mt-6 text-white/70 leading-relaxed text-base sm:text-lg">
              Sieben bewusste Schritte. Eine ehrliche Entscheidung. Begegnungen,
              die wirklich zählen.
            </p>
          </motion.div>

          {/* Inclusive */}
          <motion.div
            {...fadeUp}
            className="md:col-span-2 rounded-3xl border border-qh-line bg-white p-10 hover:-translate-y-1 transition-all duration-500 flex flex-col"
            data-testid="benefit-inclusive"
          >
            <BrandHeart className="w-10 h-10" alt="QHearts" />
            <h3 className="mt-6 font-display text-2xl sm:text-3xl font-medium tracking-tight text-qh-ink leading-tight min-h-[2.4em]">
              Inklusiv. Von Anfang an.
            </h3>
            <p className="mt-4 text-qh-slate leading-relaxed flex-1">
              QHearts wertet nicht nach Orientierung, Identität oder Herkunft.
              QHearts ist für alle Menschen da, denen Herzensangelegenheiten
              wichtig sind — ehrlich, respektvoll und ohne Schubladen.
            </p>
          </motion.div>

          {/* Comparison */}
          <motion.div
            {...fadeUp}
            className="md:col-span-4 rounded-3xl border border-qh-line bg-white p-10 hover:-translate-y-1 transition-all duration-500 flex flex-col"
            data-testid="benefit-comparison"
          >
            <BrandHeart className="w-10 h-10" alt="QHearts" />
            <h3 className="mt-6 font-display text-2xl sm:text-3xl font-medium tracking-tight text-qh-ink leading-tight min-h-[2.4em]">
              Der Swipe hat ausgedient.
            </h3>
            <p className="mt-4 text-qh-slate leading-relaxed min-h-[6.5em]">
              Ein ruhiger, kuratierter Feed. Gespräche beginnen mit Fragen —
              nicht mit einem leeren Hallo. Deine Abende gehören wieder dir.
            </p>

            <div className="mt-auto pt-8">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-6 gap-y-2 text-sm">
                <div />
                <div className="text-xs font-semibold text-qh-rose tracking-wider">
                  QHEARTS
                </div>
                <div className="text-xs font-semibold text-qh-slate tracking-wider">
                  ANDERE
                </div>

                {[
                  "Verifizierte Profile",
                  "Kostenlos nutzbar",
                  "Vielfältige Identitäten",
                  "Gesprächsstarter statt Smalltalk",
                  "Keine Werbung. Keine Paywall.",
                ].map((label) => (
                  <React.Fragment key={label}>
                    <div className="text-qh-ink">{label}</div>
                    <div className="justify-self-center">
                      <Check className="w-4 h-4 text-qh-rose" strokeWidth={2.5} />
                    </div>
                    <div className="justify-self-center">
                      <X className="w-4 h-4 text-qh-slate/60" strokeWidth={2} />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* EVENTS */}
      <Section id="events">
        <motion.div {...fadeUp} className="max-w-3xl">
          <Overline>Events</Overline>
          <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-qh-ink leading-tight">
            Wo Musik verbindet und Emotionen entstehen,
            <br />
            <span className="italic text-qh-rose">finden </span>
            <span className="text-qh-rose font-semibold not-italic">QHearts</span>
            <span className="italic text-qh-rose"> ihr Zuhause.</span>
          </h2>
          <p className="mt-6 text-lg text-qh-slate leading-relaxed">
            Die nächsten Termine für Festivals — erlebe QHearts live, triff
            echte Menschen und nimm dein QHeart persönlich in die Hand.
          </p>
        </motion.div>

        <div className="mt-12 sm:mt-14">
          <EventVideo
            src="/assets/events/dj-emotion.mp4"
            subtitle="Ein Moment auf dem Festival"
            caption="Wenn Musik Herzen trifft — und alles andere verstummt."
          />
        </div>

        <div className="mt-14 sm:mt-16 grid md:grid-cols-2 gap-6 lg:gap-8" data-testid="events-grid">
          {[
            {
              id: "gluecksgefuehle",
              src: "/assets/events/glucksgefuehle-festival.jpeg",
              name: "Glücksgefühle Festival '26",
              tagline: "Das größte Musikfestival Deutschlands",
              date: "03. – 06. September 2026",
              location: "Hockenheimring",
              link: "https://www.gluecksgefuehle-festival.de",
            },
            {
              id: "swr3",
              src: "/assets/events/swr3-newpop-festival.png",
              name: "SWR3 New Pop Festival",
              tagline: "Finde, was echt passt.",
              date: "17. – 19. September 2026",
              location: "Sieben Zwerge Park, Kaiserslautern",
              link: null,
            },
          ].map((ev, idx) => (
            <motion.a
              key={ev.id}
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
              href={ev.link || "#events"}
              target={ev.link ? "_blank" : undefined}
              rel={ev.link ? "noopener noreferrer" : undefined}
              className="group relative block rounded-3xl overflow-hidden border border-qh-line bg-white hover:-translate-y-1 transition-all duration-500 hover:shadow-[0_30px_60px_-20px_rgba(225,29,72,0.25)]"
              data-testid={`event-card-${ev.id}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-[#FFFBFB] flex items-center justify-center">
                <img
                  src={ev.src}
                  alt={ev.name}
                  className="w-full h-full object-contain transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                  draggable="false"
                />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/95 backdrop-blur px-3.5 py-1.5 border border-qh-line shadow-sm">
                  <span className="h-2 w-2 rounded-full bg-qh-rose animate-pulse" />
                  <span className="text-[11px] font-semibold tracking-wide text-qh-ink uppercase">
                    Live erleben
                  </span>
                </div>
              </div>
              <div className="p-6 sm:p-7">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-qh-rose">
                  {ev.tagline}
                </div>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl font-medium text-qh-ink leading-tight">
                  {ev.name}
                </h3>
                <div className="mt-5 flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#FFE4E6] text-qh-rose font-medium">
                    <Heart className="w-3 h-3 fill-qh-rose text-qh-rose" strokeWidth={0} />
                    {ev.date}
                  </span>
                  <span className="inline-flex items-center text-xs px-3 py-1.5 rounded-full border border-qh-line text-qh-slate">
                    {ev.location}
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mt-12 inline-flex items-center gap-3 rounded-full border border-qh-line bg-[#FFFBFB] px-5 py-2.5 text-sm text-qh-slate"
        >
          <span className="h-2 w-2 rounded-full bg-qh-rose animate-pulse" />
          Weitere Festivaltermine folgen — bleib dran.
        </motion.div>
      </Section>

      {/* LOVEZONE */}
      <Section id="lovezone" className="bg-[#FFFBFB]">
        <div className="grid lg:grid-cols-[1fr_auto] gap-10 lg:gap-16 items-start">
          <motion.div {...fadeUp} className="max-w-2xl">
            <Overline>LoveZone</Overline>
            <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-qh-ink">
              Der Ort, an dem{" "}
              <span className="italic text-qh-rose">alles beginnt.</span>
            </h2>

            <div className="mt-8 space-y-5 text-lg text-qh-ink/90 leading-relaxed">
              <p>
                LoveZone ist der Ort, an dem alles beginnt. Hier entsteht
                das, was dein Leben verändern kann.
              </p>
              <p>
                <span className="font-medium text-qh-ink">
                  Deine QHearts werden hier lebendig.
                </span>{" "}
                Du gestaltest sie, gibst ihnen Bedeutung, Worte und Gefühl. Du
                entscheidest, was du zeigen möchtest — und was die Person, der
                du es schenkst, wirklich von dir sehen darf.
              </p>
              <p>
                <span className="font-medium text-qh-ink">
                  Jedes Herz trägt einen Teil von dir.
                </span>{" "}
                Du kannst es benennen, formen und bewusst vergeben.
              </p>
              <p>Und du siehst, was daraus wird:</p>
              <ul className="space-y-2 pl-1">
                <li className="flex items-start gap-3">
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                  <span>Welche Herzen ankommen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                  <span>Welche Verbindungen entstehen.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-qh-rose shrink-0" />
                  <span>Und welche ihren Weg nicht finden.</span>
                </li>
              </ul>
              <div className="pt-2 border-l-2 border-qh-rose/40 pl-5 italic font-display text-2xl sm:text-[26px] leading-snug text-qh-ink">
                LoveZone ist kein gewöhnlicher Raum.
                <br />
                Es ist <span className="text-qh-rose">ehrlich</span>.
                <br />
                Es ist <span className="text-qh-rose">direkt</span>.
                <br />
                Und es ist der Anfang von etwas{" "}
                <span className="text-qh-rose">Echtem</span>.
              </div>
            </div>

            <div className="mt-10">
              <Link to="/lovezone">
                <Button
                  className="rounded-full bg-qh-rose hover:bg-qh-rosedark text-white px-7 h-12 text-sm font-medium transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                  data-testid="lovezone-cta"
                >
                  Zu LoveZone
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="hidden lg:flex items-center justify-center w-[280px] h-[280px] rounded-[40px] bg-gradient-to-br from-[#FFF1F3] via-white to-[#FFE4E6] border border-qh-line shadow-[0_30px_60px_-30px_rgba(225,29,72,0.25)] shrink-0"
            data-testid="lovezone-visual"
          >
            <BrandHeart className="w-44 h-44 opacity-95" alt="" />
          </motion.div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section id="testimonials">
        <motion.div {...fadeUp} className="max-w-2xl">
          <Overline>Love-Stories</Overline>
          <h2 className="mt-4 font-display font-medium text-4xl sm:text-5xl tracking-tight text-qh-ink">
            Herzen, die <span className="italic">angekommen</span> sind.
          </h2>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-3 gap-6" data-testid="testimonials">
          {[
            {
              quote:
                "Zum ersten Mal seit Jahren hatte ich ein Gespräch, das nicht mit einem leeren Hallo begann. Wir sind jetzt seit acht Monaten zusammen.",
              name: "Melanie",
              role: "31, Leipzig",
            },
            {
              quote:
                "Endlich eine App, in der queer zu sein kein Nachgedanke ist. Ich habe mich ab dem ersten Screen gesehen gefühlt.",
              name: "Mike",
              role: "27, Berlin",
            },
            {
              quote:
                "Ich habe ein QHeart bekommen — und plötzlich war da mehr als nur Text. Es war persönlich. Echt. Und hat mich wirklich berührt.",
              name: "Anita",
              role: "28, München",
            },
          ].map((t, i) => (
            <motion.figure
              key={t.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
              className="rounded-3xl border border-qh-line bg-white p-8 flex flex-col"
              data-testid={`testimonial-${i + 1}`}
            >
              <Quote className="w-8 h-8 text-qh-rose" strokeWidth={1.2} />
              <blockquote className="mt-4 font-display text-xl sm:text-2xl text-qh-ink leading-snug">
                "{t.quote}"
              </blockquote>
              <figcaption className="mt-6 pt-6 border-t border-qh-line flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold text-qh-ink">{t.name}</div>
                  <div className="text-qh-slate">{t.role}</div>
                </div>
                <div className="flex gap-0.5">
                  {["s1", "s2", "s3", "s4", "s5"].map((k) => (
                    <Star
                      key={k}
                      className="w-3.5 h-3.5 fill-qh-rose text-qh-rose"
                      strokeWidth={0}
                    />
                  ))}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </Section>

      {/* DOWNLOAD CTA */}
      <Section id="download" className="bg-[#FFFBFB]">
        <motion.div {...fadeUp} className="max-w-3xl">
          <Overline>Bereit, wenn du es bist</Overline>
          <h2 className="mt-4 font-display font-medium text-5xl sm:text-6xl tracking-tight text-qh-ink">
            Bereit, jemand <span className="italic text-qh-rose">Echten</span> zu treffen?
          </h2>
          <p className="mt-5 text-lg text-qh-slate leading-relaxed">
            Trag dich auf die Warteliste ein. Deine Einladung kommt, sobald QHearts
            auf iOS und Android startet.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
            <button
              onClick={openComingSoon}
              className="group flex items-center gap-3 rounded-2xl border border-qh-line bg-white hover:bg-qh-ink hover:text-white hover:border-qh-ink transition-all duration-500 px-6 py-3.5 hover:-translate-y-0.5"
              data-testid="download-app-store"
            >
              <Apple className="w-7 h-7" strokeWidth={1.3} />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-qh-slate group-hover:text-white/70">
                  Laden im
                </div>
                <div className="font-display font-semibold text-lg -mt-0.5">
                  App Store
                </div>
              </div>
            </button>

            <button
              onClick={openComingSoon}
              className="group flex items-center gap-3 rounded-2xl border border-qh-line bg-white hover:bg-qh-ink hover:text-white hover:border-qh-ink transition-all duration-500 px-6 py-3.5 hover:-translate-y-0.5"
              data-testid="download-play-store"
            >
              <Smartphone className="w-7 h-7" strokeWidth={1.3} />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-wider text-qh-slate group-hover:text-white/70">
                  Jetzt bei
                </div>
                <div className="font-display font-semibold text-lg -mt-0.5">
                  Google Play
                </div>
              </div>
            </button>
          </div>

          <div className="mt-12 max-w-lg">
            <p className="text-sm text-qh-slate mb-4">
              Oder lass uns deine E-Mail-Adresse da — wir schicken die Einladung
              direkt in dein Postfach.
            </p>
            <WaitlistForm compact />
          </div>
        </motion.div>
      </Section>

      {/* FOOTER */}
      <footer className="bg-qh-ink text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="grid md:grid-cols-2 gap-10 items-start">
            <div>
              <img
                src="/assets/qhearts-logo-dark.png"
                alt="QHearts – Keep Dating real."
                className="w-64 sm:w-80 h-auto object-contain select-none"
                draggable="false"
              />
              <p className="mt-6 font-display italic text-lg sm:text-xl leading-tight whitespace-nowrap">
                Selbstbestimmt statt fremdbestimmt.
              </p>
              <p className="mt-1 font-display italic text-lg sm:text-xl leading-tight whitespace-nowrap">
                Du entscheidest – nicht der Algorithmus.
              </p>
              <p className="mt-5 font-display italic text-lg sm:text-xl leading-tight whitespace-nowrap text-white/80">
                Echte Begegnungen beginnen mit deiner Entscheidung.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
              <div>
                <div className="text-white/40 uppercase tracking-widest text-xs mb-4">Produkt</div>
                <ul className="space-y-3">
                  <li><a href="#how" className="hover:text-qh-rose transition-colors">Wie es funktioniert</a></li>
                  <li><a href="#safety" className="hover:text-qh-rose transition-colors">Sicherheit</a></li>
                  <li><a href="#benefits" className="hover:text-qh-rose transition-colors">Warum wir</a></li>
                </ul>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-xs mb-4">Unternehmen</div>
                <ul className="space-y-3">
                  <li><a href="#" className="hover:text-qh-rose transition-colors">Über uns</a></li>
                  <li><a href="#" className="hover:text-qh-rose transition-colors">Karriere</a></li>
                  <li><a href="#" className="hover:text-qh-rose transition-colors">Presse</a></li>
                </ul>
              </div>
              <div>
                <div className="text-white/40 uppercase tracking-widest text-xs mb-4">Rechtliches</div>
                <ul className="space-y-3">
                  <li>
                    <Link
                      to="/datenschutz"
                      className="hover:text-qh-rose transition-colors"
                      data-testid="footer-datenschutz-link"
                    >
                      Datenschutz
                    </Link>
                  </li>
                  <li>
                    <a
                      href="/agb"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-qh-rose transition-colors"
                      data-testid="footer-agb-link"
                    >
                      AGB
                    </a>
                  </li>
                  <li>
                    <a
                      href="/impressum"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-qh-rose transition-colors"
                      data-testid="footer-impressum-link"
                    >
                      Impressum
                    </a>
                  </li>
                  <li>
                    <button
                      onClick={openSettings}
                      className="hover:text-qh-rose transition-colors text-left"
                      data-testid="footer-cookie-settings"
                    >
                      Cookie-Einstellungen ändern
                    </button>
                  </li>
                  <li><a href="#" className="hover:text-qh-rose transition-colors">Richtlinien</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-white/50">
            <div>© {new Date().getFullYear()} QHearts. Mit Sorgfalt gemacht.</div>
            <div>Gemacht für echte Herzen.</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
